import { Component, OnInit, ViewChild } from '@angular/core';
import { InfoService } from '../../service/info.service';
import { NavbarComponent } from '../../component/navbar/navbar.component';
import { NavbarService } from '../../service/navbar.service';
import { SidebarService } from '../../service/sidebar.service';
import { SidebarItem } from '../../model/sidebar-item';
import { NotificationService } from '../../service/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../service/user.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit {
  @ViewChild('navbar', { static: true }) public navbar: NavbarComponent;

  public isMobile: boolean;
  // Estado inicial: mostra só os itens sempre visíveis (Eventos e
  // Eventos que coordeno). Os demais aparecem quando `/badges/`
  // devolver os flags `hasEver.*`.
  public items: SidebarItem[] = this.buildItems({});

  constructor(
    public infoService: InfoService,
    private navbarService: NavbarService,
    private sidebarService: SidebarService,
    public notificationService: NotificationService,
    private translateService: TranslateService,
    private userService: UserService,
  ) {
    this.isMobile = window.innerWidth < 768;
  }

  ngOnInit(): void {
    this.sidebarService.setSidebar(undefined, this);
    this.navbarService.setNavbar(this.navbar);

    // `getUserFromToken` mantido pra popular InfoService/perfil em geral,
    // mas a visibilidade dos itens vem exclusivamente do `/badges/`.
    this.userService.getUserFromToken().subscribe();
    this.refreshAllBadges();
  }

  /**
   * Um único fetch (`/core/user/badges/`) alimenta todos os badges.
   * Substitui 4 requests em paralelo que baixavam listas inteiras só
   * pra contar — cada seção agora é um `.count()` server-side.
   * Público: `session-chairings.component.ts` chama após o usuário
   * alterar a apresentação (no-show) na página, mantendo o badge em sync.
   */
  public refreshAllBadges(): void {
    this.userService.getBadges().subscribe(
      data => {
        // Rebuild dos items com os flags `hasEver.*` — visibilidade
        // dos botões condicionais só depois do backend responder.
        this.items = this.buildItems(data.hasEver || {});
        this.applyBadges(data);
      },
      () => { /* silencioso — badges ausentes em falha são aceitáveis */ }
    );
  }

  /**
   * DEPRECATED: renomeado pra `refreshAllBadges()`. Mantido pra não
   * quebrar chamadas externas legadas em session-chairings; delega.
   */
  public refreshSessionChairingsBadges(): void {
    this.refreshAllBadges();
  }

  private applyBadges(data: {
    reviews:          { notified: number; confirmed: number };
    submissions:      { pending: number };
    committees:       { pendingInvites: number };
    sessionChairings: { pendingNoShow: number };
  }): void {
    const tt = (key: string, count: number) => this.translateService.instant(key, { count });

    const reviewsItem = this.items.find(i => i.class === 'button-my-reviews');
    if (reviewsItem) {
      const { notified, confirmed } = data.reviews;
      reviewsItem.badges = [
        {
          statusClass: 'notified', count: notified,
          tooltip: tt(`menus.sidebar.my-reviews.badge.pending-invites${notified === 1 ? '' : '-plural'}`, notified),
          link: '/reviews', queryParams: { preset: 'invites-pending' },
        },
        {
          statusClass: 'confirmed', count: confirmed,
          tooltip: tt(`menus.sidebar.my-reviews.badge.confirmed-reviews${confirmed === 1 ? '' : '-plural'}`, confirmed),
          link: '/reviews', queryParams: { preset: 'confirmed-reviews' },
        },
      ];
    }

    const submissionsItem = this.items.find(i => i.class === 'button-my-submissions');
    if (submissionsItem) {
      const { pending } = data.submissions;
      submissionsItem.badges = [
        {
          statusClass: 'pending', count: pending,
          tooltip: tt(`menus.sidebar.my-submissions.badge.pending${pending === 1 ? '' : '-plural'}`, pending),
          link: '/submissions', queryParams: { preset: 'drafts' },
        },
      ];
    }

    const committeeItem = this.items.find(i => i.class === 'button-tpcs');
    if (committeeItem) {
      const pending = data.committees.pendingInvites;
      committeeItem.badges = [
        {
          statusClass: 'notified', count: pending,
          tooltip: tt(`menus.sidebar.my-committees.badge.pending-invites${pending === 1 ? '' : '-plural'}`, pending),
        },
      ];
    }

    const sessionItem = this.items.find(i => i.class === 'button-session-chairings');
    if (sessionItem) {
      const pending = data.sessionChairings.pendingNoShow;
      sessionItem.badges = [
        {
          statusClass: 'notified', count: pending,
          tooltip: tt(`menus.sidebar.my-session-chairings.badge.pending-noshow${pending === 1 ? '' : '-plural'}`, pending),
          link: '/session-chairings',
        },
      ];
    }
  }

  /**
   * Constrói a lista de itens da sidebar seguindo as regras de
   * visibilidade combinadas:
   *   - Eventos               → SEMPRE
   *   - Eventos que coordeno  → SEMPRE
   *   - Minhas submissões     → só se hasEver.submissions
   *   - Minhas revisões       → só se hasEver.reviews
   *   - Sessões que coordeno  → só se hasEver.sessionChairings
   *   - Publicações que coord.→ só se hasEver.publicationChairings
   *   - TPCs que participo    → só se hasEver.committees
   *
   * `flags` vem do `/core/user/badges/`. Enquanto o backend não
   * responde (1º render), o objeto vem vazio e só os "SEMPRE" aparecem.
   */
  private buildItems(flags: Partial<{
    submissions: boolean;
    reviews: boolean;
    sessionChairings: boolean;
    publicationChairings: boolean;
    committees: boolean;
  }>): SidebarItem[] {
    const items: SidebarItem[] = [];

    items.push(new SidebarItem('/events', { name: 'emoji_objects', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.events.full', short: 'menus.sidebar.events.short' }, { class: 'button-events' }));

    if (flags.submissions) {
      items.push(new SidebarItem('/submissions', { name: 'description', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-submissions.full', short: 'menus.sidebar.my-submissions.short' }, { class: 'button-my-submissions' }));
    }
    if (flags.reviews) {
      items.push(new SidebarItem('/reviews', { name: 'balance', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-reviews.full', short: 'menus.sidebar.my-reviews.short' }, { class: 'button-my-reviews' }));
    }
    if (flags.sessionChairings) {
      items.push(new SidebarItem('/session-chairings', { name: 'co_present', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-session-chairings.full', short: 'menus.sidebar.my-session-chairings.short' }, { class: 'button-session-chairings' }));
    }
    if (flags.publicationChairings) {
      items.push(new SidebarItem('/publication-chairings', { name: 'menu_book', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-publication-chairings.full', short: 'menus.sidebar.my-publication-chairings.short' }, { class: 'button-publication-chairings' }));
    }
    if (flags.committees) {
      items.push(new SidebarItem('/committees', { name: 'groups', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-committees.full', short: 'menus.sidebar.my-committees.short' }, { class: 'button-tpcs' }));
    }

    // "Eventos que coordeno" — sempre visível (regra de UX; a página
    // trata vazio via empty-state).
    items.push(new SidebarItem('/my-events', { name: 'star', font: 'material-symbols-outlined' }, { full: 'menus.sidebar.my-events.full', short: 'menus.sidebar.my-events.short' }, { class: 'button-chairings' }));

    return items;
  }
}
